import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import axios from "axios";

// Helper for Textarea if not in UI lib
const TextareaInput = (props) => <textarea className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50" {...props} />

const PartnerIntakeForm = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    // Company Info
    company_name: "",
    website_url: "",
    primary_contact_name: "",
    contact_email: "",
    contact_phone: "",
    head_office_location: "",
    
    // Business Overview
    years_in_operation: "",
    num_employees: "",
    primary_industry: "",
    company_overview: "",
    
    // Service & Experience
    operates_ne_alberta: "no",
    indigenous_experience: "no",
    
    // Partnership Intent
    partnership_type: "", // Single select for MVP simplicity, or multi if robust
    reason_for_partnering: ""
  });
  
  const [logoFile, setLogoFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      let logoUrl = "";
      
      // 1. Upload Logo if present
      if (logoFile) {
        const uploadData = new FormData();
        uploadData.append("file", logoFile);
        const uploadRes = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/files/upload`, uploadData, {
            headers: { "Content-Type": "multipart/form-data" }
        });
        logoUrl = uploadRes.data.url;
      }

      // 2. Map to backend schema (PartnerSignup)
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/register-partner`, {
        company_name: formData.company_name,
        contact_email: formData.contact_email,
        phone: formData.contact_phone,
        password: "TempPassword123!", // Auto-gen for intake form
        logo_url: logoUrl,
        
        // Full intake data packed in description for now
        description: `
          Website: ${formData.website_url}
          Head Office: ${formData.head_office_location}
          Years: ${formData.years_in_operation} | Employees: ${formData.num_employees}
          Industry: ${formData.primary_industry}
          Overview: ${formData.company_overview}
          Operates NE AB: ${formData.operates_ne_alberta}
          Indigenous Exp: ${formData.indigenous_experience}
          Intent: ${formData.reason_for_partnering}
        `
      });
      setSuccess(true);
    } catch (e) {
      console.error(e);
      alert("Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto mb-16">
      <Button 
        onClick={() => setIsOpen(!isOpen)} 
        className="w-full h-16 text-lg font-heading font-bold uppercase tracking-widest bg-[#D97706] hover:bg-[#B45309] text-white rounded-none flex items-center justify-between px-8 shadow-lg"
      >
        <span>Become a Partner</span>
        {isOpen ? <ChevronUp /> : <ChevronDown />}
      </Button>

      {isOpen && (
        <Card className="mt-4 rounded-none border-t-4 border-[#D97706] animate-in slide-in-from-top-4 duration-500">
          <CardContent className="p-8">
            {success ? (
              <div className="text-center py-12">
                <h3 className="text-2xl font-bold text-green-600 mb-4">Application Submitted</h3>
                <p className="text-gray-600">Thank you for your interest. Our economic development team will review your profile and contact you shortly.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* 1. Company Info */}
                <div>
                  <h3 className="font-heading font-bold text-lg uppercase mb-4 text-zinc-400 border-b pb-2">Company Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label>Company Name *</Label>
                      <Input required value={formData.company_name} onChange={e => setFormData({...formData, company_name: e.target.value})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Website URL</Label>
                      <Input value={formData.website_url} onChange={e => setFormData({...formData, website_url: e.target.value})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Primary Contact Name *</Label>
                      <Input required value={formData.primary_contact_name} onChange={e => setFormData({...formData, primary_contact_name: e.target.value})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Contact Email *</Label>
                      <Input type="email" required value={formData.contact_email} onChange={e => setFormData({...formData, contact_email: e.target.value})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Phone Number *</Label>
                      <Input required value={formData.contact_phone} onChange={e => setFormData({...formData, contact_phone: e.target.value})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Head Office Location</Label>
                      <Input value={formData.head_office_location} onChange={e => setFormData({...formData, head_office_location: e.target.value})} />
                    </div>
                    <div className="col-span-2 space-y-2">
                      <Label>Company Logo (Optional)</Label>
                      <Input type="file" accept="image/*" onChange={e => setLogoFile(e.target.files[0])} />
                    </div>
                  </div>
                </div>

                {/* 2. Business Overview */}
                <div>
                  <h3 className="font-heading font-bold text-lg uppercase mb-4 text-zinc-400 border-b pb-2">Business Overview</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label>Years in Operation</Label>
                      <Select onValueChange={v => setFormData({...formData, years_in_operation: v})}>
                        <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="0-2">0–2 Years</SelectItem>
                          <SelectItem value="3-5">3–5 Years</SelectItem>
                          <SelectItem value="6-10">6–10 Years</SelectItem>
                          <SelectItem value="10+">10+ Years</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Number of Employees</Label>
                      <Select onValueChange={v => setFormData({...formData, num_employees: v})}>
                        <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1-10">1–10</SelectItem>
                          <SelectItem value="11-50">11–50</SelectItem>
                          <SelectItem value="51-200">51–200</SelectItem>
                          <SelectItem value="200+">200+</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2 space-y-2">
                      <Label>Primary Industry Sector</Label>
                      <Select onValueChange={v => setFormData({...formData, primary_industry: v})}>
                        <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Construction">Construction</SelectItem>
                          <SelectItem value="Energy">Energy Services</SelectItem>
                          <SelectItem value="Environmental">Environmental</SelectItem>
                          <SelectItem value="Civil">Civil Works</SelectItem>
                          <SelectItem value="Logistics">Logistics</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2 space-y-2">
                      <Label>Company Overview</Label>
                      <TextareaInput placeholder="Brief summary of your company..." value={formData.company_overview} onChange={e => setFormData({...formData, company_overview: e.target.value})} />
                    </div>
                  </div>
                </div>

                {/* 3. Service & Experience */}
                <div>
                  <h3 className="font-heading font-bold text-lg uppercase mb-4 text-zinc-400 border-b pb-2">Experience</h3>
                  <div className="space-y-6">
                    <div className="space-y-2">
                      <Label className="block mb-2">Do you currently operate in NE Alberta?</Label>
                      <RadioGroup onValueChange={v => setFormData({...formData, operates_ne_alberta: v})} className="flex gap-4">
                        <div className="flex items-center space-x-2"><RadioGroupItem value="yes" id="op-yes" /><Label htmlFor="op-yes">Yes</Label></div>
                        <div className="flex items-center space-x-2"><RadioGroupItem value="no" id="op-no" /><Label htmlFor="op-no">No</Label></div>
                      </RadioGroup>
                    </div>
                    <div className="space-y-2">
                      <Label className="block mb-2">Experience working with Indigenous Communities?</Label>
                      <RadioGroup onValueChange={v => setFormData({...formData, indigenous_experience: v})} className="flex gap-4">
                        <div className="flex items-center space-x-2"><RadioGroupItem value="yes" id="ind-yes" /><Label htmlFor="ind-yes">Yes</Label></div>
                        <div className="flex items-center space-x-2"><RadioGroupItem value="no" id="ind-no" /><Label htmlFor="ind-no">No</Label></div>
                      </RadioGroup>
                    </div>
                  </div>
                </div>

                {/* 4. Partnership Intent */}
                <div>
                  <h3 className="font-heading font-bold text-lg uppercase mb-4 text-zinc-400 border-b pb-2">Partnership Intent</h3>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Why do you want to partner with FLFN?</Label>
                      <TextareaInput value={formData.reason_for_partnering} onChange={e => setFormData({...formData, reason_for_partnering: e.target.value})} />
                    </div>
                  </div>
                </div>

                <div className="pt-6">
                  <Button type="submit" size="lg" className="w-full bg-primary text-black hover:bg-yellow-500 font-bold uppercase tracking-widest" disabled={submitting}>
                    {submitting ? "Submitting..." : "Submit Application"}
                  </Button>
                  <p className="text-xs text-center text-gray-400 mt-4">
                    By submitting this form, you agree to our privacy policy and partnership terms.
                  </p>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PartnerIntakeForm;
